import { useState } from "react";
import { Radar as RadarIcon, ArrowRight, Plus, MessageSquare } from "lucide-react";
import { useDashboard } from "@/components/layout/AppShell";
import { PageHeader, EmptyState } from "@/components/PageState";
import { SourceBadge } from "@/components/SourceBadge";
import { InsightPanel } from "@/components/InsightPanel";
import { PRIORITY_META, QUADRANTS } from "@/lib/orbit";
import { trackEvent } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

function Matrix({ opportunities, onSelect }) {
  const size = 420;
  const pad = 44;
  const plot = size - pad * 2;
  // effort 1..3 -> x ; impact 1..3 -> y (inverted)
  const pos = (score) => pad + ((score - 1) / 2) * plot;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-auto" data-testid="impact-effort-matrix">
      <line x1={size / 2} y1={pad} x2={size / 2} y2={size - pad} stroke="hsl(var(--border))" strokeWidth="1" />
      <line x1={pad} y1={size / 2} x2={size - pad} y2={size / 2} stroke="hsl(var(--border))" strokeWidth="1" />
      {[["Quick Wins", pad + 6, pad + 16, "start"], ["Strategic Bets", size - pad - 6, pad + 16, "end"], ["Low Priority", pad + 6, size - pad - 8, "start"], ["Complex / Later", size - pad - 6, size - pad - 8, "end"]].map(([t, x, y, anchor]) => (
        <text key={t} x={x} y={y} textAnchor={anchor} fontSize="11" fontWeight="600" fill="hsl(var(--muted-foreground))">{t}</text>
      ))}
      <text x={size / 2} y={size - 10} textAnchor="middle" fontSize="11" fill="hsl(var(--muted-foreground))">Implementation effort →</text>
      <text x={14} y={size / 2} textAnchor="middle" fontSize="11" fill="hsl(var(--muted-foreground))" transform={`rotate(-90 14 ${size / 2})`}>Business impact →</text>
      {opportunities.map((o) => {
        const x = pos(o.effort_score);
        const y = size - pos(o.impact_score);
        const c = o.priority === "Immediate" ? "hsl(0 72% 48%)" : o.priority === "Quick Win" ? "hsl(160 84% 30%)" : o.priority === "Strategic" ? "hsl(224 71% 45%)" : "hsl(215 16% 55%)";
        return (
          <g key={o.id} style={{ cursor: "pointer" }} onClick={() => onSelect(o)} data-testid={`matrix-node-${o.id}`}>
            <circle cx={x} cy={y} r="9" fill={c} fillOpacity="0.18" stroke={c} strokeWidth="2" />
            <circle cx={x} cy={y} r="3" fill={c} />
          </g>
        );
      })}
    </svg>
  );
}

export default function OpportunityRadar() {
  const { data, loading } = useDashboard();
  const [selected, setSelected] = useState(null);

  if (loading) return <Skeleton className="h-96 rounded-2xl" />;

  if (!data?.has_data) {
    return (
      <div>
        <PageHeader eyebrow="Prioritise" title="Opportunity Radar" />
        <EmptyState icon={RadarIcon} title="Opportunities appear after your assessment"
          whatsMissing="ORBIT identifies opportunities from your business evidence, which comes from the assessment."
          whyItMatters="This is where diagnosis becomes action — the highest-value, most realistic things to do next." />
      </div>
    );
  }

  const opps = data.opportunities;
  const evidence = selected ? (data.domains.find((d) => d.key === selected.domain)?.evidence || []) : [];

  const openOpp = (o) => {
    setSelected(o);
    trackEvent("OPPORTUNITY_VIEWED", { opportunity: o.id });
  };

  return (
    <div>
      <PageHeader eyebrow="Prioritise" title="Opportunity Radar"
        description="The highest-value transformation opportunities, ranked by impact, effort and readiness.">
        <SourceBadge type="rule" />
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="rounded-2xl border border-border bg-card p-6 lg:col-span-2">
          <h3 className="mb-1 font-display text-base font-semibold">Impact vs Effort</h3>
          <p className="mb-4 text-sm text-muted-foreground">Consulting-style prioritisation. Select any point for detail.</p>
          <Matrix opportunities={opps} onSelect={openOpp} />
        </div>

        <div className="space-y-4 lg:col-span-3">
          {opps.map((o) => (
            <button key={o.id} onClick={() => openOpp(o)} className="block w-full rounded-2xl border border-border bg-card p-5 text-left transition-colors duration-200 hover:border-accent/40" data-testid={`opportunity-card-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-lg font-semibold">{o.title}</h3>
                <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${PRIORITY_META[o.priority]?.bg} ${PRIORITY_META[o.priority]?.text} ${PRIORITY_META[o.priority]?.border}`}>{o.priority}</span>
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{o.problem}</p>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
                  <span><span className="text-muted-foreground">Impact </span><b>{o.impact}</b></span>
                  <span><span className="text-muted-foreground">Effort </span><b>{o.effort}</b></span>
                  <span><span className="text-muted-foreground">Readiness </span><b>{o.readiness}</b></span>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-accent">Details <ArrowRight className="h-3.5 w-3.5" /></span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex flex-wrap items-center gap-2">
                  <DialogTitle className="font-display text-xl">{selected.title}</DialogTitle>
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${PRIORITY_META[selected.priority]?.bg} ${PRIORITY_META[selected.priority]?.text} ${PRIORITY_META[selected.priority]?.border}`}>{selected.priority}</span>
                </div>
                <DialogDescription className="sr-only">Detailed transformation analysis for {selected.title}</DialogDescription>
              </DialogHeader>
              <div className="space-y-5 pt-2">
                <p className="text-sm leading-relaxed text-muted-foreground">{selected.problem}</p>

                <div className="grid grid-cols-3 gap-2 text-center">
                  {[["Impact", selected.impact], ["Effort", selected.effort], ["Readiness", selected.readiness]].map(([k, v]) => (
                    <div key={k} className="rounded-xl border border-border bg-background p-3">
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</div>
                      <div className="mt-0.5 font-display text-base font-semibold">{v}</div>
                    </div>
                  ))}
                </div>

                <TransformFlow current={selected.current_state} future={selected.future_state} />

                <Detail badge="responses" title="Why this opportunity?" body={selected.why} />

                {evidence.length > 0 && (
                  <div>
                    <div className="mb-1.5 flex items-center gap-2">
                      <h4 className="font-display text-sm font-semibold">What evidence supports it?</h4>
                      <SourceBadge type="responses" />
                    </div>
                    <ul className="space-y-1.5">
                      {evidence.map((e) => (
                        <li key={e.question} className="rounded-lg border border-border bg-background p-2.5 text-sm">
                          <span className="text-muted-foreground">{e.question}</span>
                          <span className="mt-0.5 block font-medium">→ {e.answer}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Detail badge="ai" title="What could change?" body={selected.what_could_change} />
                <Detail badge="ai" title="Potential business impact" body={selected.impact_detail} />
                <Detail badge="calculated" title="What would implementation require?" body={selected.effort_detail} />
                <Detail badge="calculated" title="Readiness" body={selected.readiness_detail} />
                <Detail badge="ai" title="Recommended approach" body={selected.approach} />
                <Detail badge="verify" title="Risks & assumptions" body={selected.risks} />
                <div className="rounded-xl border border-accent/30 bg-accent/5 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-accent">Recommended next step</p>
                  <p className="mt-1 text-sm">{selected.next_step}</p>
                </div>

                <InsightPanel key={selected.id} operation="opportunity" payload={{ opportunity_id: selected.id }} heading="Why ORBIT identified this" variant="opportunity" testid="insight-opportunity" />

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button className="flex-1" onClick={() => { trackEvent("OPPORTUNITY_ADDED_TO_ROADMAP", { opportunity: selected.id }); toast.success("Added to your Transformation Roadmap"); }} data-testid="add-to-roadmap-btn">
                    <Plus className="h-4 w-4" /> Add to Transformation Roadmap
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => { trackEvent("CONSULTATION_REQUESTED", { opportunity: selected.id }); toast.success("Request noted", { description: "A MYTHIVA™ consultant will be able to follow up." }); }} data-testid="discuss-implementation-btn">
                    <MessageSquare className="h-4 w-4" /> Discuss Implementation
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TransformFlow({ current = [], future = [] }) {
  if (!current.length && !future.length) return null;
  const Row = ({ label, steps, tone }) => (
    <div>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {steps.map((s, i) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${tone === "future" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300" : "border-border bg-background text-muted-foreground"}`}>{s}</span>
            {i < steps.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground/60" />}
          </span>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-4 rounded-xl border border-border bg-secondary/40 p-4" data-testid="transform-flow">
      <Row label="Current state" steps={current} tone="current" />
      <div className="flex justify-center"><ArrowRight className="h-4 w-4 rotate-90 text-accent" /></div>
      <Row label="Transformed state" steps={future} tone="future" />
    </div>
  );
}

function Detail({ badge, title, body }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <h4 className="font-display text-sm font-semibold">{title}</h4>
        <SourceBadge type={badge} />
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}
